import AddCommentIcon from "@mui/icons-material/AddComment";
import SortIcon from "@mui/icons-material/Sort";

import { CircularProgress } from "@mui/material";
import Grid from "@mui/material/Grid";
import { unwrapResult } from "@reduxjs/toolkit";
import BackdropPrimaryMain from "components/BackdropPrimaryMain";
import ClickableText from "components/ClickableText/ClickableText";
import { AddCommentDialog } from "components/comments/AddCommentDialog";
import { Comment } from "components/comments/Comment";
import { EditCommentDialog } from "components/comments/EditCommentDialog";
import PrimaryButton from "components/PrimaryButton";
import { TabProps } from "models/TabProps";
import { CommentType } from "operations/schema/schema";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useAppDispatch, useAppSelector } from "store";
import { selectPageSelectedJob } from "store/root.store";
import {
  addComment,
  deleteComment,
  getMentionables,
  updateComment,
} from "store/slices/comments.store";
import { getComments } from "store/slices/planner.store";

export const PlannerCommentsTab: FC<TabProps> = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const { comments, id: jobId, taskCreator } = useAppSelector(selectPageSelectedJob);
  const { loading, mentionables } = useAppSelector((s) => s.comments);
  const { commentsLoading } = useAppSelector((s) => s.planner);
  const [showAddComment, setShowAddComment] = useState<boolean>(false);
  const [showEditComment, setShowEditComment] = useState<boolean>(false);

  const [sortNewestFirst, setSortNewestFirst] = useState<boolean>(true);
  const [selectedComment, setSelectedComment] = useState<CommentType | null>(null);

  const sortedComments = useMemo(() => {
    let newComments = comments ? [...comments] : [];
    newComments = newComments
      ? newComments.sort((a, b) => {
          if (!sortNewestFirst) {
            return (
              new Date(a.changedDate ?? a.createdDate ?? new Date()).getTime() -
              new Date(b.changedDate ?? b.createdDate ?? new Date()).getTime()
            );
          } else {
            return (
              new Date(b.changedDate ?? b.createdDate ?? new Date()).getTime() -
              new Date(a.changedDate ?? a.createdDate ?? new Date()).getTime()
            );
          }
        })
      : [];
    return newComments;
  }, [comments, sortNewestFirst]);

  const handleSave = useCallback(
    async (text: string) => {
      await dispatch(
        addComment({
          comment: {
            commentId: null,
            jobId: jobId,
            source: "service",
            text,
          },
        }),
      );
      dispatch(getComments());
      setShowAddComment(false);
    },
    [dispatch, jobId],
  );

  const handleUpdate = useCallback(
    async (comment: CommentType) => {
      await dispatch(
        updateComment({
          comment: {
            commentId: comment.commentId,
            sourceGuid: comment.sourceGuid,
            text: comment.text,
            jobId: jobId,
            source: "service",
          },
        }),
      );
      dispatch(getComments());
      setShowEditComment(false);
      setSelectedComment(null);
    },
    [dispatch, jobId],
  );

  const handleDelete = useCallback(
    (commentId: number) => {
      dispatch(
        deleteComment({
          commentId,
        }),
      )
        .then(unwrapResult)
        .then(() => {
          dispatch(getComments());
        });
    },
    [dispatch],
  );

  const setEditMode = useCallback((comment: CommentType) => {
    setSelectedComment(comment);
    setShowEditComment(true);
  }, []);

  useEffect(() => {
    dispatch(getMentionables());
  }, [dispatch]);

  const defaultMention = useMemo(() => {
    var dataId = "";
    var label = "";
    if (mentionables.defaultTaskCreator) {
      if (!taskCreator) return;
      var user = mentionables.users?.find((u) => u.loginName === taskCreator);
      if (!user || !user.loginName) return;
      dataId = `user;;${user.loginName}`;
      label = user.fullName ?? "";
    } else if (mentionables.defaultMainServiceTeam) {
      var team = mentionables.teams?.find((t) => t.teamCode === mentionables.mainServiceTeamCode);
      if (!team) return;
      dataId = `team;;${team.teamGuid}`;
      label = [team.teamCode, team.teamDescription].join(" - ");
    }
    return `<span data-type="mention" data-id="${dataId}" data-label="${label}" data-mention-suggestion-char="@" contenteditable="false">@${label}</span><br>`;
  }, [mentionables, taskCreator]);

  return (
    <Grid container direction="column" data-testid="CommentsTab">
      <BackdropPrimaryMain open={loading || commentsLoading}>
        <CircularProgress color="inherit" />
      </BackdropPrimaryMain>
      <Grid
        sx={{
          paddingTop: "20px",
          paddingBottom: "20px",
          display: "flex",
          justifyContent: "flex-end",
          flex: 1,
        }}
      >
        <ClickableText
          onClick={() => {
            setSortNewestFirst(!sortNewestFirst);
          }}
        >
          {sortNewestFirst
            ? intl.formatMessage({ id: "comments.newestFirst" })
            : intl.formatMessage({ id: "comments.oldestFirst" })}
          <SortIcon />
        </ClickableText>
      </Grid>
      <Grid>
        {sortedComments?.map((x) => (
          <Comment
            key={x.commentId}
            commentIn={x}
            handleDelete={handleDelete}
            setEditMode={setEditMode}
          />
        ))}
      </Grid>
      <Grid style={{ margin: "16px", marginBottom: "0" }}>
        <PrimaryButton
          startIcon={<AddCommentIcon />}
          onClick={() => setShowAddComment(true)}
          style={{ textTransform: "none" }}
        >
          <FormattedMessage id="comments.addComment" />
        </PrimaryButton>
      </Grid>
      <AddCommentDialog
        open={showAddComment}
        defaultMention={defaultMention}
        handleClose={() => setShowAddComment(false)}
        handleSave={handleSave}
      />
      <EditCommentDialog
        open={showEditComment}
        comment={selectedComment}
        handleClose={() => setShowEditComment(false)}
        handleUpdate={handleUpdate}
      />
    </Grid>
  );
};
